import { isLoading } from "apollo/utils";
import { useEnvContext } from "context/EnvContext";
import { APP_ACP, REDIRECT_PARAM } from "core/consts";
import { isAdminLogged } from "core/model/accounts";
import { getQueryVariable } from "core/model/utils/urls";
import { useManualSetLegalDocuments } from "dsl/atoms/LegalDocuments";
import { useHandleLogin } from "dsl/ecosystems/CareproviderOnboardingLoginPage/useHandleLogin";
import { LoginPage } from "dsl/ecosystems/LoginPage/LoginPage";
import { useAcpHandlerUrls } from "dsl/hooks/useNavigationHandlers";
import { acpAuthPath, defaultRedirectPath } from "entries/acp/Router";
import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAppSelector } from "reduxentities/hooks";

export default function AcpAuthPage() {
  const logged = useAppSelector(isAdminLogged);
  const location = useLocation();
  const redirectTo = getQueryVariable(location.search, REDIRECT_PARAM);
  const { env } = useEnvContext();
  const {
    challengeTimestamp,
    handleLogin,
    loginErrors,
    loginView,
    progressLogin,
    resetError,
    setLoginView,
    setProgressLogin,
  } = useHandleLogin();
  useManualSetLegalDocuments();
  const { goToForgotPassword: goToForgotPasswordUrl, goToProductionEnv } =
    useAcpHandlerUrls();

  return logged === "ok" ? (
    <Navigate replace to={redirectTo ?? defaultRedirectPath} />
  ) : (
    <main>
      <LoginPage
        app={APP_ACP}
        challengeTimestamp={challengeTimestamp}
        env={env}
        goToForgotPassword={goToForgotPasswordUrl()}
        goToProductionUrl={goToProductionEnv()}
        isLoading={isLoading(progressLogin)}
        loginErrors={loginErrors}
        loginView={loginView}
        resetError={resetError}
        onBackToLogin={() => {
          resetError();
          setLoginView("login");
        }}
        onSubmit={(payload) => handleLogin(payload, setProgressLogin)}
      />
    </main>
  );
}

export function AcpAuthenticate({ children }: { children: ReactNode | null }) {
  const logged = useAppSelector(isAdminLogged);
  const { pathname, search } = useLocation();
  const params = new URLSearchParams(search);
  params.delete(REDIRECT_PARAM);
  const cleanSearch = params.toString();
  const redirectTo = encodeURIComponent(
    `${pathname}${cleanSearch ? `?${cleanSearch}` : ""}`,
  );

  return logged === "ok" ? (
    <>{children}</>
  ) : (
    <Navigate
      replace
      to={{ pathname: acpAuthPath, search: `${REDIRECT_PARAM}=${redirectTo}` }}
    />
  );
}
